// Copyright (C) 2022 Cochise Ruhulessin
//
// All rights reserved. No warranty, explicit or implicit, provided. In
// no event shall the author(s) be liable for any claim or damages.
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
import { useEffect, useState } from 'react'
import { useLocales } from 'react-localized'
import { IonItem, IonItemGroup, IonLabel, IonList, IonNote, IonSegment, IonSegmentButton } from '@ionic/react'
import * as iso3166 from 'i18n-iso-countries'
import {defaultCountry} from './l10n'


interface CountryListProps {
  children?: React.ReactNode
  heading: string
  selected?: string | null
  onSelected(code: string): void
}


const filters: {[key: string]: string[]} = {
  EEA: [
    "AT", "BE", "BG", "CY", "CZ", "DE", "DK", "EE", "GR", "ES",
    "FI", "FR", "HR", "HU", "IT", "IE", "LT", "LU", "LV", "MT",
    "PL", "PT", "RO", "SE", "SI", "SK", "NL"
  ],
  EUR: [
    "AD", "AL", "AZ", "BA", "BY", "CH", "CY", "GB", "GE", "HR",
    "IS", "KZ", "LI", "MC", "MD", "ME", "MK", "NO", "RU", "TR",
    "UA", "VA", "XK",
  ],
  NAFTA: ["CA", "MX", "US"]
}


const CountryList: React.FC<CountryListProps> = ({children, heading, onSelected, selected}) => {
  const {gettext, locale} = useLocales()
  const countries: {[key: string]: any} = iso3166.getNames(locale, {select: "alias"})
  const country = selected || defaultCountry || "NL"
  const [activeFilter, setActiveFilter] = useState<string>(
    (!selected && filters.EEA.includes(country))
    ? "EEA"
    : "ALL"
  )

  return (
    <IonList mode="md">
      {children}
      <IonItem
        detail
        onClick={() => onSelected(country)}
        style={{cursor: "pointer", marginBottom: "11px"}}
      >
        <IonLabel>{iso3166.getName(country, locale)}</IonLabel>
        <IonNote slot="helper">{gettext("Tik om verder te gaan.")}</IonNote>
      </IonItem>
      <IonItem lines="none" style={{"--padding-start": "20px"}}>
        <IonLabel><h2 style={{fontSize: "18px"}}>{heading}</h2></IonLabel>
      </IonItem>
      <IonItem lines="none">
        <IonSegment value={activeFilter} onIonChange={(event) => setActiveFilter(event.detail.value || "ALL")}>
          <IonSegmentButton value="EEA">
            <IonLabel>{gettext("European Union (EU)")}</IonLabel>
          </IonSegmentButton>
          <IonSegmentButton value="EUR">
            <IonLabel>{gettext("Europe (non-EU)")}</IonLabel>
          </IonSegmentButton>
        </IonSegment>
      </IonItem>
      <IonItem>
        <IonSegment value={activeFilter} onIonChange={(event) => setActiveFilter(event.detail.value || "ALL")}>
          <IonSegmentButton value="NAFTA">
            <IonLabel>{gettext("N. America")}</IonLabel>
          </IonSegmentButton>
          <IonSegmentButton value="ALL">
            <IonLabel>{gettext("All countries")}</IonLabel>
          </IonSegmentButton>
        </IonSegment>
      </IonItem>
      <IonItemGroup>
        {Object.keys(countries).map((code) => {
          let country = countries[code]
          if (activeFilter !== "ALL" && !filters[activeFilter].includes(code)) {
            return null
          }
          return (
            <IonItem
              detail
              key={code}
              onClick={() => onSelected(code)}
              style={{cursor: "pointer"}}
            >
              <IonLabel>{country}</IonLabel>
            </IonItem>
        )})}
      </IonItemGroup>
    </IonList>
  )
}


export { CountryList }